/**
 * The paywall (D165): a wall needs words, words need a wall, and a wall on
 * a game that ships whole is a sign, not a wall.
 *
 * @remarks
 * Two failures and one warning. A pack with a `paid` room and no `paywall`
 * has a refusal with nothing to say — the platform would have to speak in
 * its own voice at the one moment the vision forbids it. A pack with a
 * `paywall` and no paid room carries words no turn can reach: the D47
 * family, dead prose. And a `client` game's pack ships to the browser
 * whole (D132: readable by anyone with devtools), so `paid` there gates
 * nothing — reported, not refused, because a creator may mark rooms before
 * flipping the game to `server`.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, RoomId } from '@orpheus/content-schema';

import type { ReferenceBrokenFailure } from './load-failure.js';

import { brokenReference } from './semantic-validation.js';

/** A client game with paid rooms: the pack ships whole, so the marking gates nothing. */
export interface PaidRoomsOnClientGame {
	readonly roomIds: readonly RoomId[];
}

/** The rooms past the demo, in declaration order. */
export const paidRoomIdsOf = (contentPack: ContentPackDefinition): readonly RoomId[] =>
	contentPack.rooms.flatMap((room) => (room.access === 'paid' ? [room.roomId] : []));

/**
 * The two failures: a wall without words, words without a wall.
 *
 * @param contentPack - The schema-valid pack
 * @returns The failures, empty when the wall and the words agree
 */
export const validatePaywallAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const paidRoomIds = paidRoomIdsOf(contentPack);
	const [firstPaid] = paidRoomIds;

	if (firstPaid !== undefined && contentPack.paywall === undefined) {
		return [
			brokenReference(
				'paywall',
				firstPaid,
				`rooms are marked access: 'paid' (${paidRoomIds.map((roomId) => `"${roomId}"`).join(', ')}) but the pack has no paywall — the turn that would cross needs the game's own line, not the platform's`,
			),
		];
	}

	if (firstPaid === undefined && contentPack.paywall !== undefined) {
		return [brokenReference('paywall.narratives', 'paywall', 'the pack has paywall lines but no room is marked access: \'paid\' — words no turn can reach')];
	}

	return [];
};

/**
 * The warning: a client game's paid rooms gate nothing.
 *
 * @param contentPack - The schema-valid pack
 * @returns The warning, or nothing
 */
export const findPaidRoomsOnClientGame = (contentPack: ContentPackDefinition): PaidRoomsOnClientGame | undefined => {
	const roomIds = paidRoomIdsOf(contentPack);

	return contentPack.manifest.play === 'client' && roomIds.length > 0 ? { roomIds } : undefined;
};
